/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import PropTypes from 'prop-types';

// @mui material components
// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "nextus/Dashboards/DashboardLayout";
import DashboardNavbar from "nextus/Dashboards/DashboardNavbar";
import Footer from "examples/Footer";

import Map from "nextus/Maps/Map";

// Calendar application components
import Header from "nextus/Calendars/ClearwayCalendar/components/Header";
import NextClearwayEvents from "nextus/Calendars/ClearwayCalendar/components/NextClearwayEvents";

// Data
import { useNavigate, useMatch } from 'react-router-dom';

import DataTable from "nextus/Tables/DevicesTable";
import { calculateBattery } from "helper/device";

import { QueryClient, QueryClientProvider, useQuery, useMutation } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import groupAPI from 'api/groups';
import bksAPI from 'api/bks';
import clearwaysAPI from 'modules/clearways/api';
import userAPI from "api/user";

import EventForm from "modules/clearways/components/EventForm";

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import ClearwayTimeline from "nextus/Timelines/ClearwayTimeline";
import { processDevicesData } from "helper/device";
import NextEvent from "modules/clearways/views/NextEvent";
import { getUserAccessLevel } from "helper/user";
import { processGroupData } from "helper/group";
import CameraLatest from "nextus/CameraLatest";
import { Box, Tab, Tabs } from "@mui/material";
import { tabsClasses } from '@mui/material/Tabs';

const mapbox_settings = {
    style: 'mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true',
    zoom: 17,
    // maxZoom: 5
}

function wrapper(content) {
    return (
        <SoftBox pt={{ xxs: 2, xs: 3 }} pb={{ xxs: 2, xs: 3 }} height={{ xxs: 'calc(100% - 32px)', xs: 'calc(100% - 48px)' }}
            sx={({ palette: { white }, boxShadows: { navbarBoxShadow }, borders: { borderRadius }, functions: { rgba, pxToRem } }) => ({
                boxShadow: navbarBoxShadow,
                backdropFilter: `saturate(200%) blur(${pxToRem(4)})`,
                //   backgroundColor: rgba(white.main, 0.8),
                borderRadius: borderRadius.xl,
                // height: 'calc(100% - 50px)',
                overflow: 'auto',

                '::-webkit-scrollbar': {
                    backgroundColor: 'transparent',
                    width: '8px',
                },
                '::-webkit-scrollbar-track': { /* background of the scrollbar except button or resizer */
                    backgroundColor: 'transparent',
                },
                '::-webkit-scrollbar-thumb': { /* scrollbar itself */
                    backgroundColor: '#babac0',
                    borderRadius: '6px',
                    border: `1px solid ${rgba(white.main, 0.8)}`,
                },
                '::-webkit-scrollbar-button': { /* set button(top and bottom of the scrollbar) */
                    display: 'none',
                },
            })}
        >
            {content}
        </SoftBox>
    )
}

function updateGroup(data) {
    console.log(data);
    console.log("TO DO... UPLOAD");
    saveGroupWithID(data.group_id, data);
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            style={{ height: 'calc(100% - 35px)' }}
        >
            {value === index && <Box sx={{ px: { xxs: 2, sm: 3 }, pt: { xxs: 2, sm: 3 }, pb: 0, height: 'calc(100% - 24px)' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}


function DeviceGroupView({ map, navTitle, setNavTitle, sidebarState, setSidebarState }) {
    groupAPI.loadToken(useAuth0());
    bksAPI.loadToken(useAuth0());
    clearwaysAPI.loadToken(useAuth0());
    userAPI.loadToken(useAuth0());

    const { user } = useAuth0();

    const date = moment();
    const month = date.month();
    const year = date.year();
    const navigate = useNavigate();
    const { group_id } = useMatch('groups/:group_id').params;

    const [tabValue, setTabValue] = useState(0);

    const handleTabClick = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const onRowClicked = function (row, event) {
        console.log(row);
        console.log(event);
        goToDevicePage(row.original.uuid);
    }

    const goToDevicePage = (uuid) => {
        let path = '../../devices/' + uuid;
        console.log(navigate)
        navigate(path);
    }

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await bksAPI.getAllDevices();
        }
    });

    const group = useQuery({
        queryKey: ['getGroupWithID', group_id],
        queryFn: async () => {
            return await groupAPI.getGroupWithID(group_id);
        }
    });

    const clearway = useQuery({
        queryKey: ['getClearwaysWithYearAndMonth2', year, month],
        queryFn: async () => {
            return await clearwaysAPI.getClearwaysWithYearAndMonth(year, month);
        },
        staleTime: 20 * 1000
    });

    const selected_user = useQuery({
        queryKey: ['getUserByEmail', user.email.toLowerCase()],
        queryFn: async () => {
            return await userAPI.getUserByEmail(user.email.toLowerCase());
        }
    })

    // const { mutate } = useMutation({
    //     mutationFn: (values) => updateEvent(values),
    // })

    useEffect(() => {
        setSidebarState('sidenav');

        if (map.current) {
            map.current.flyTo({
                center: [0, 0],
                zoom: 2,
                essential: true
            });
        }
    }, [sidebarState]);

    if (group.isLoading || selected_user.isLoading) {
        return wrapper("Loading");
    }

    if (group.error || selected_user.error) {
        return wrapper("Error!");
    }

    if (!group.data || !devices.data || !clearway.data || !selected_user.data) {
        return wrapper('Loading');
    }

    var group_data = processGroupData(group.data, devices.data, getUserAccessLevel(selected_user.data.user.user_type, 'technician'));

    var data_table_data = {
        columns: [],
        rows: group_data.devices
    }

    if (getUserAccessLevel(selected_user.data.user.user_type, 'technician')) {
        data_table_data.columns = [
            { Header: 'Name', accessor: 'name' },
            { Header: 'Site ID', accessor: 'site_id' },
            { Header: 'Battery', accessor: 'battery' },
            { Header: 'Temperature', accessor: 'temperature' },
            { Header: 'Humidity', accessor: 'humidity' },
            { Header: 'RSSI', accessor: 'rssi' },
            { Header: 'Serial Number', accessor: 'serial_number' },
            {
                Header: 'Last Updated',
                accessor: 'last_updated',
                sortType: function (rowa, rowb) {
                    if (rowa.original.last_heartbeat > rowb.original.last_heartbeat) {
                        return 1;
                    }
                    else if (rowa.original.last_heartbeat < rowb.original.last_heartbeat) {
                        return -1;
                    }
                    else {
                        return 0;
                    }
                }
            }
        ];
    }
    else {
        data_table_data.columns = [
            { Header: 'Name', accessor: 'name' },
            { Header: 'Site ID', accessor: 'site_id' },
            { Header: 'Battery', accessor: 'battery' },
            { Header: 'Temperature', accessor: 'temperature' },
            { Header: 'RSSI', accessor: 'rssi' },
            { Header: 'Serial Number', accessor: 'serial_number' }
        ];
    }

    if (!group_data.all_updated) {
        var polygon_colour = "#ff667c";
    }
    else if (group_data.devices_monitored > 0 && getUserAccessLevel(selected_user.data.user.user_type, 'technician')) {
        var polygon_colour = "#edaa00";
    }
    else {
        var polygon_colour = "#58ba2d";
    }

    // console.log(group.data)
    return wrapper(
        <SoftBox sx={{ overflow: "visible", backgroundColor: 'transparent', font: '12px / 20px Helvetica Neue, Arial, Helvetica, sans-serif' }}>
            <Grid container>
                <Grid item xxs={12} sm={12} md={12} lg={12} xl={12}>
                    <Tabs value={tabValue} onChange={handleTabClick} aria-label="DeviceView Tabs"
                        allowScrollButtonsMobile
                        variant="scrollable"
                        scrollButtons
                        sx={{
                            [`& .${tabsClasses.scrollButtons}`]: {
                                '&.Mui-disabled': { opacity: 0.3 },
                            },
                        }}
                    >
                        <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Overview" {...a11yProps(0)} />
                        <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Camera Feeds" {...a11yProps(1)} />
                        {getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (
                            <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Group Settings" {...a11yProps(4)} />
                        )}
                    </Tabs>
                </Grid>
            </Grid>
            <CustomTabPanel value={tabValue} index={0}>
                <Grid container spacing={3}>
                    <Grid item xxs={12} xl={7.5} >
                        <Card >
                            <SoftBox p={2} lineHeight={1} mb={2}>
                                <SoftTypography pl={1} pt={1} variant="h4" textTransform="capitalize">
                                    {group.data.name}
                                </SoftTypography>
                                <SoftTypography pl={1} pt={1} variant="h6" fontWeight="regular" color="text">
                                    {group_data.devices.length + " Devices"}
                                </SoftTypography>
                                <DataTable table={data_table_data} user_role={selected_user.data.user.user_type} onRowClicked={(row, event) => onRowClicked(row, event)} canSearch />
                            </SoftBox>
                        </Card>
                    </Grid>
                    <Grid item xxs={12} xl={4.5} >
                        <Grid item xxs={12} xl={12} id="map" pb={3}>
                            <Card onClick={(e) => { e.stopPropagation() }} sx={{ height: '240px' }}>
                                <Map selected_group={group_data} parent_group={group_data.parent_group} allow_devices={true} colour={polygon_colour} settings={mapbox_settings} padding={30} user_role={selected_user.data.user.user_type} />
                            </Card>
                        </Grid>
                        <Grid item xxs={12} xl={12} id="timeline" >
                            <SoftBox onClick={(e) => { e.stopPropagation() }}>
                                {/* <ClearwayTimeline data={clearway.data} groupID={group_id} /> */}
                                <NextEvent data={clearway.data} group_name={group_data.name} navTitle={navTitle} setNavTitle={setNavTitle} groupID={group_id} />
                            </SoftBox>
                        </Grid>
                    </Grid>
                </Grid>
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={1}>
                <Grid item xxs={12} xl={12}>
                    <Card>
                        <Grid container>
                            {group.data.devices.map(function (row, index) {
                                return (<Grid xxs={12} sm={6} m={4} lg={3} xl={2} key={index}>
                                    <Card >
                                        <SoftTypography pl={1} pt={1} variant="h6" fontWeight="regular" color="text">
                                            {row.name}
                                        </SoftTypography>
                                        <SoftTypography pl={1} pt={1} variant="h6" fontWeight="regular" color="text">
                                            {row.serial_number}
                                        </SoftTypography>
                                        <CameraLatest uuid={row.uuid} />
                                    </Card>
                                </Grid>)
                            })}
                        </Grid>
                    </Card>
                </Grid>
            </CustomTabPanel>
        </SoftBox>
    );
}

DeviceGroupView.propTypes = {
    map: PropTypes.object,
    navTitle: PropTypes.array,
    setNavTitle: PropTypes.func,
    sidebarState: PropTypes.string,
    setSidebarState: PropTypes.func,
}

export default DeviceGroupView;